import { AxiosError, AxiosInstance, AxiosRequestHeaders, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ApiLogger } from '../../workers/Logger/interface';
import { trimAll } from './helpers';

/**
 * Middleware for Axios to provide request and response logging.
 */
class AxiosLoggingMiddleware {
    #axiosInstance: AxiosInstance;
    #logger: ApiLogger;
    #namespace: string;
    #ignoreNamespace?: string;

    /**
     * Constructs the AxiosLoggingMiddleware.
     * @param {AxiosInstance} axiosInstance - The Axios instance to attach the middleware to.
     * @param {ApiLogger} logger - The logger instance to use for logging.
     * @param {string} namespace - A namespace for the logger to categorize logs.
     * @param {string} ignoreNamespace - A namespace for the logger to ignore data from.
     */
    constructor(axiosInstance: AxiosInstance, logger: ApiLogger, namespace: string, ignoreNamespace?: string) {
        this.#axiosInstance = axiosInstance;
        this.#logger = logger;
        this.#namespace = namespace;
        this.#ignoreNamespace = ignoreNamespace;

        this.#attachInterceptors();
    }

    /**
     * Attaches request and response interceptors to the Axios instance.
     * @private
     */
    #attachInterceptors() {
        this.#axiosInstance.interceptors.request.use(
            (config: InternalAxiosRequestConfig) => this.#logRequest(config),
            error => this.#logRequestError(error)
        );

        this.#axiosInstance.interceptors.response.use(
            response => this.#logResponse(response),
            error => this.#logResponseError(error)
        );
    }

    /**
     * Logs request details.
     * @param {InternalAxiosRequestConfig} config - The Axios request configuration.
     * @returns {InternalAxiosRequestConfig}
     * @private
     */
    #logRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
        const startTime = Date.now();
        if (!config.headers) {
            config.headers = {} as AxiosRequestHeaders;
        }
        config.headers['X-Request-Time'] = startTime;
        return config;
    }

    /**
     * Logs request errors.
     * @param {AxiosError} error - The Axios error object.
     * @returns {Promise<AxiosError>}
     * @private
     */
    #logRequestError(error: AxiosError): Promise<AxiosError> {
        this.#logError(error, 'Request Error');
        return Promise.reject(error);
    }

    /**
     * Logs response details.
     * @param {AxiosResponse} response - The Axios response.
     * @returns {AxiosResponse}
     * @private
     */
    #logResponse(response: AxiosResponse): AxiosResponse {
        const requestTime = this.#calculateRequestTime(response);
        this.#logBasedOnTime(response, requestTime);
        trimAll(response.data);
        return response;
    }

    /**
     * Logs response errors.
     * @param {AxiosError} error - The Axios error object.
     * @returns {Promise<AxiosError>}
     * @private
     */
    #logResponseError(error: AxiosError): Promise<AxiosError> {
        this.#logError(error, 'Response Error');
        return Promise.reject(error);
    }

    /**
     * Calculates the time taken for a request.
     * @param {AxiosResponse | AxiosError} responseOrError - The Axios response or error object.
     * @returns {number}
     * @private
     */
    #calculateRequestTime(responseOrError: AxiosResponse | AxiosError): number {
        const startTime = parseInt(responseOrError.config?.headers?.['X-Request-Time'] as string, 10) || Date.now();
        return Date.now() - startTime;
    }

    /**
     * Logs messages based on request time.
     * @param {AxiosResponse} response - The Axios response.
     * @param {number} requestTime - The time taken for the request.
     * @private
     */
    #logBasedOnTime(response: AxiosResponse, requestTime: number) {
        let logLevel: 'error' | 'warn' | 'debug';
        if (requestTime > 5000) {
            logLevel = 'error';
        } else if (requestTime > 1000) {
            logLevel = 'warn';
        } else {
            logLevel = 'debug';
        }

        const logData = {
            method: response.config?.method,
            params: response.config?.params,
            data: response?.data && this.#ignoreNamespace !== this.#namespace
                ? response.data : undefined,
        };

        this.#logger[logLevel](this.#namespace, this.#formatLogMessage(response), response.status, requestTime, logData);
    }

    /**
     * Logs errors.
     * @param {AxiosError} error - The Axios error object.
     * @param {string} messageType - The type of message to log.
     * @private
     */
    #logError(error: AxiosError, messageType: string) {
        const requestTime = this.#calculateRequestTime(error);
        this.#logger.error(
            this.#namespace,
            `${messageType}: ${error.config?.method} ${error.config?.url} `,
            Number(error.response?.status) || -1,
            requestTime,
            {
                code: error.code,
                config: error.config,
            }
        );
    }

    /**
     * Formats a log message.
     * @param {AxiosResponse} response - The Axios response.
     * @returns {string}
     * @private
     */
    #formatLogMessage(response: AxiosResponse): string {
        return `${response.config.method} ${response.config.url}`;
    }
}

export default AxiosLoggingMiddleware;
